/**
 * The onboarding wizard's rules (A8, Dan's frames 15185:5272 to 15185:5898; Dan 2026-10-02):
 * who it opens for, the order of its steps, the values it pre-fills and the name a firm gets
 * when the field is left empty. Pure, so Home stays wiring.
 */
import type { Me } from "@contract/firm/me";
import type { CurrencyCode } from "@contract/standards/formats";
import { firmNamed } from "../homeModel";
import type { StandardsDraft } from "../standardsModel";

export const ONBOARDING_STEPS = ["name", "colors", "fonts", "labels", "signIn"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** The name a firm gets when the admin leaves the field empty (Dan 2026-10-02). */
const FALLBACK_FIRM_NAME = "My Firm";

/**
 * What the colors and fonts steps open with, saved as they stand on Continue (Dan 2026-10-02:
 * pre-fill, and save everything). For now these are the add-in's own defaults (the product
 * theme's accents and fonts, the Brand Profile's 20 and 12 pt); Dan supplies the final
 * values.
 */
export const ONBOARDING_PREFILL: Pick<StandardsDraft, "seriesColors" | "headingFont" | "bodyFont" | "headingSize" | "bodySize"> = {
  seriesColors: ["#032249", "#A83434", "#596879", "#424242", "#ACACAC", "#F0F0F0"],
  headingFont: "Arial",
  bodyFont: "Arial",
  headingSize: 20,
  bodySize: 12,
};

/** The Labels step's opening values (Dan 2026-10-02): the currency from the browser's locale
 * (below), written "$1.2M"; months "Mar 25"; fiscal years "FY25", ending in December. */
const ONBOARDING_LABELS: Pick<StandardsDraft, "currencyStyle" | "calendarYearFormat" | "fiscalStyle" | "fiscalEndMonth"> = {
  currencyStyle: "symbol-short",
  calendarYearFormat: "mon-yy",
  fiscalStyle: "fy-yy",
  fiscalEndMonth: "12",
};

const EURO_REGIONS = ["AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK"];

/** The supported currencies by region (ISO 3166). */
const REGION_CURRENCY: Record<string, CurrencyCode> = {
  US: "USD", GB: "GBP", AU: "AUD", CA: "CAD", NZ: "NZD", CH: "CHF", LI: "CHF", JP: "JPY", CN: "CNY",
  HK: "HKD", SG: "SGD", IN: "INR", SE: "SEK", NO: "NOK", DK: "DKK", ZA: "ZAR", AE: "AED",
  ...Object.fromEntries(EURO_REGIONS.map((r) => [r, "EUR" as CurrencyCode])),
};

/** The currency for the browser's languages: the first whose region (stated, or the likely one
 * for a bare language: "de" is Germany) has a supported currency. USD otherwise (Dan
 * 2026-10-02). */
export function currencyForLocales(locales: readonly string[]): CurrencyCode {
  for (const tag of locales) {
    try {
      const region = new Intl.Locale(tag).maximize().region;
      if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region]!;
    } catch {
      // Not a valid language tag: try the next.
    }
  }
  return "USD";
}

/** The browser's languages, most preferred first. */
export function browserLocales(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/** The admins who can both name the firm and set its standards. */
function canOnboard(me: Me): boolean {
  return me.status === "active" && me.capabilities.includes("manage_members") && me.capabilities.includes("edit_standards");
}

/** A new firm: its name is still the placeholder and nobody has saved standards. The wizard
 * opens on its own only then (and not again once the admin skips it in this browser). */
export function isNewFirm(me: Me): boolean {
  return canOnboard(me) && !firmNamed(me) && me.firmVersion === 0;
}

/** The name to save: what was typed, trimmed, else the fallback. */
export function firmNameToSave(typed: string): string {
  return typed.trim() || FALLBACK_FIRM_NAME;
}

/** The draft with the pre-fill applied where the firm has nothing of its own yet. */
export function withPrefill(draft: StandardsDraft, locales: readonly string[] = []): StandardsDraft {
  const next = { ...draft };
  if (draft.currency === "") {
    next.currency = currencyForLocales(locales);
    next.currencyStyle = ONBOARDING_LABELS.currencyStyle;
  }
  if (draft.calendarYearFormat === "") next.calendarYearFormat = ONBOARDING_LABELS.calendarYearFormat;
  if (draft.fiscalStyle === "") {
    next.fiscalStyle = ONBOARDING_LABELS.fiscalStyle;
    next.fiscalEndMonth = ONBOARDING_LABELS.fiscalEndMonth;
  }
  if (draft.seriesColors.every((c) => !c.trim())) next.seriesColors = ONBOARDING_PREFILL.seriesColors.slice();
  if (!draft.headingFont.trim()) next.headingFont = ONBOARDING_PREFILL.headingFont;
  if (!draft.bodyFont.trim()) next.bodyFont = ONBOARDING_PREFILL.bodyFont;
  if (draft.headingSize === "") next.headingSize = ONBOARDING_PREFILL.headingSize;
  if (draft.bodySize === "") next.bodySize = ONBOARDING_PREFILL.bodySize;
  return next;
}

/** "Welcome Dan, ..." with a name on file, "Welcome, ..." without (an email-code sign-up). */
export function welcomeTitle(name: string | null): string {
  return name ? `Welcome ${firstName(name)}, let's set up your profile and brand standards` : "Welcome, let's set up your profile and brand standards";
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** Where the dismissal is remembered, per user (a convenience: the wizard can always be
 * reopened from Get Started). */
export const dismissedKey = (userId: string) => `slidebrain.onboarding.dismissed.${userId}`;
